import querystring from 'query-string'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { axiosFetch, isAxiosCancellation, isAxiosResponseError } from '@halo/_utils/axios'
import { Singleton, Container } from 'typescript-ioc/es5'
import { AccountDto } from '@halo/dtos/account.dto'
import { DomainDto } from '@halo/dtos/domain.dto'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { fetchBillingRecordsByAccountId, fetchSubscriberHistoryByAccountId } from '../_utils/account'
import aqe from '@pushly/aqe'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { ApiKeyDto } from '@halo/dtos/user-with-key.dto'

@Singleton
export class AccountService {
    private appState: AppState
    private appService: AppService

    public constructor() {
        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public async fetchAll(opts: IApiCallOptions = {}): Promise<IApiResponse<any[]>> {
        let ok = false
        let accounts: any = []
        let meta: any

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts.query ?? {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/accounts?${options}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts?.cancellationKey,
            )
            const { data, ...resMeta } = res.data

            ok = true
            accounts = data
            meta = resMeta
        } catch (error) {}

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: accounts, meta }
    }

    public async fetchBillable(
        opts?: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<any[]>> {
        let ok = false
        let billable: any = []
        let meta: any

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts || {})
        const serviceURL = `${aqe.defaults.internalApiDomain}/v1/accounts/billable?${options}`

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                cancellationKey,
            )

            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            ok = true
            billable = req.data.data
            meta = req.data.meta
        } catch (error) {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }

        return { ok, data: billable, meta }
    }

    public async fetchById(
        accountId: number,
        opts?: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<AccountDto> {
        let dto: AccountDto

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/accounts/${accountId}?${options}`

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                cancellationKey,
            )

            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            dto = AccountDto.fromApiResponse(req.data.data)
        } catch (error) {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        return dto!
    }

    public async create(
        createDto: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<AccountDto>> {
        let ok = false
        let org: AccountDto

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/accounts`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: createDto,
                },
                cancellationKey,
            )

            ok = true
            org = AccountDto.fromApiResponse(req.data.data)

            simpleNotification('success', `Organization '${org.name}' has been successfully created.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: org }
    }

    public async patch(
        accountId: number,
        updateDto: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<AccountDto>> {
        let ok = false
        let org: AccountDto

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        // USING PUB API Endpoint to enable cascading functionality
        // Contains all the same functionality as previously contained in the INT_API
        const serviceURL = `${aqe.defaults.publicApiDomain}/accounts/${accountId}`

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: updateDto,
                },
                cancellationKey,
            )

            ok = true
            org = AccountDto.fromApiResponse(req.data.data)
            simpleNotification('success', `Organization '${org.name}' has been successfully updated.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: org }
    }

    public async patchBillingData(
        accountId: number,
        billingDto: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<any>> {
        let ok = false
        let dto: any = {}

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.internalApiDomain}/v1/accounts/${accountId}/billing-data`

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: billingDto,
                },
                cancellationKey,
            )

            ok = true
            dto = req.data.data
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: dto! }
    }

    public async fetchBillingRecordsByAccountId(
        accountId: number,
        opts: any = {},
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<any[]>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const response = await fetchBillingRecordsByAccountId(accountId, opts, cancellationKey)
        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async fetchBillingRecordLogs(
        accountId: number,
        recordId: number,
        opts: any = {},
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<any[]>> {
        let ok = false
        let logs: any[] = []
        let meta: any

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.internalApiDomain}/v1/accounts/${accountId}/billing-records/${recordId}/logs`

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                    data: opts,
                },
                cancellationKey,
            )

            ok = true
            logs = req.data.data || []
            meta = req.data.meta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: logs, meta }
    }

    public async createBillingRecordForAccountId(
        accountId: number,
        recordDto: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<any>> {
        let ok = false
        let data: any

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceURL = `${aqe.defaults.internalApiDomain}/v1/accounts/${accountId}/billing-records`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: recordDto,
                },
                cancellationKey,
            )

            ok = true
            data = req.data.data
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data }
    }

    public async confirmBillingRecord(
        tokenizedEndpoint: string,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<any>> {
        let ok = false
        let data: any

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceURL = `${aqe.defaults.internalApiDomain}${tokenizedEndpoint}`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                },
                cancellationKey,
            )

            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            ok = true
            data = req.data.data
        } catch (error) {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        return { ok, data }
    }

    public async fetchDomains(accountId: number, opts: IApiCallOptions = {}): Promise<IApiResponse<DomainDto[]>> {
        let ok = false
        let dtos: DomainDto[] = []
        let meta: any

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/accounts/${accountId}/domains?${options}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            const { data, ...resMeta } = res.data

            ok = true
            dtos = data.map(DomainDto.fromApiResponse)
            meta = resMeta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: dtos, meta }
    }

    public async fetchSubscriberHistoryByAccountId(
        accountId: number,
        opts?: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<any[]>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const response = await fetchSubscriberHistoryByAccountId(accountId, opts, cancellationKey)
        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async fetchInternalApiUsers(opts: IApiCallOptions = {}): Promise<IApiResponse<ApiKeyDto[]>> {
        let response: IApiResponse<ApiKeyDto[]> = {
            ok: false,
            data: [],
        }

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceUrl = `${aqe.defaults.publicApiDomain}/system/api-keys`

        try {
            const req = await axiosFetch('get', {
                url: serviceUrl,
            })

            if (opts.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            const { data: apiUser, ...reqMeta } = req.data

            response.ok = true
            response.data = apiUser.data.map(ApiKeyDto.fromApiResponse)
            response.meta = reqMeta
        } catch (error) {
            response.error = error
            response.cancelled = isAxiosCancellation(error)

            if (!response.cancelled) {
                simpleNotification('error', error.response?.data?.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async regenerateInternalApiKey(
        apiKey: string,
        opts?: IApiCallOptions,
    ): Promise<IApiResponse<ApiKeyDto | null>> {
        let response: IApiResponse<ApiKeyDto | null> = {
            ok: false,
            data: null,
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceUrl = `${aqe.defaults.publicApiDomain}/system/api-keys/${apiKey}/regenerate`

        try {
            const req = await axiosFetch('post', {
                url: serviceUrl,
            })

            const { data: apiUser, ...reqMeta } = req.data
            response.ok = true
            response.data = apiUser
            response.meta = reqMeta
        } catch (error) {
            response.error = error
            response.cancelled = isAxiosCancellation(error)

            if (!response.cancelled) {
                simpleNotification('error', error.response?.data?.message)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }
}
